/**
 * Component Variants
 * Reusable style configurations for common component patterns
 */

import { colors, spacing, borderRadius, shadows, typography } from './designTokens';

// ==================== BUTTON VARIANTS ====================

export const buttonVariants = {
  // Primary action button
  primary: {
    type: 'primary',
    style: {
      fontWeight: typography.fontWeight.medium,
    },
  },

  // Secondary/default button
  default: {
    type: 'default',
    style: {
      fontWeight: typography.fontWeight.medium,
    },
  },

  // Danger/destructive action
  danger: {
    type: 'primary',
    danger: true,
    style: {
      fontWeight: typography.fontWeight.medium,
    },
  },

  // Ghost/minimal button
  ghost: {
    type: 'ghost',
    style: {
      fontWeight: typography.fontWeight.medium,
    },
  },

  // Link style button
  link: {
    type: 'link',
    style: {
      fontWeight: typography.fontWeight.normal,
    },
  },

  // Custom action button (replaces .custom-button class)
  customAction: {
    type: 'default',
    style: {
      fontWeight: typography.fontWeight.medium,
      transition: 'all 0.2s ease',
    },
    className: 'custom-button',
  },
};

// ==================== STATUS TAG VARIANTS ====================

/**
 * Get tag color based on status value
 * Replaces scattered status coloring logic
 */
export const getStatusTagColor = (status) => {
  const statusLower = String(status).toLowerCase();

  const colorMap = {
    // Pass/Success states
    pass: 'success',
    passed: 'success',
    success: 'success',
    active: 'success',
    enabled: 'success',
    completed: 'success',
    online: 'success',

    // Fail/Error states
    fail: 'error',
    failed: 'error',
    error: 'error',
    inactive: 'error',
    disabled: 'error',
    offline: 'error',

    // Warning states
    warning: 'warning',
    pending: 'warning',
    in_progress: 'processing',
    running: 'processing',
    syncing: 'processing',

    // Info/Unknown states
    unknown: 'default',
    not_evaluated: 'default',
    info: 'blue',
  };

  return colorMap[statusLower] || 'default';
};

/**
 * Get severity tag color and style
 */
export const getSeverityTagProps = (severity) => {
  const severityLower = String(severity).toLowerCase();

  const severityMap = {
    critical: {
      color: colors.severity.critical.main,
      style: {
        backgroundColor: colors.severity.critical.bg,
        borderColor: colors.severity.critical.border,
        color: colors.severity.critical.main,
        fontWeight: typography.fontWeight.semibold,
      },
    },
    high: {
      color: colors.severity.high.main,
      style: {
        backgroundColor: colors.severity.high.bg,
        borderColor: colors.severity.high.border,
        color: colors.severity.high.main,
        fontWeight: typography.fontWeight.medium,
      },
    },
    medium: {
      color: colors.severity.medium.main,
      style: {
        backgroundColor: colors.severity.medium.bg,
        borderColor: colors.severity.medium.border,
        color: colors.severity.medium.main,
      },
    },
    low: {
      color: colors.severity.low.main,
      style: {
        backgroundColor: colors.severity.low.bg,
        borderColor: colors.severity.low.border,
        color: colors.severity.low.main,
      },
    },
  };

  return severityMap[severityLower] || {
    style: {
      backgroundColor: colors.neutral[100],
      borderColor: colors.neutral[300],
      color: colors.text.secondary,
    },
  };
};

// ==================== CARD VARIANTS ====================

export const cardVariants = {
  // Default card (subtle border, minimal shadow)
  default: {
    bordered: true,
    style: {
      borderRadius: borderRadius.md,
      boxShadow: shadows.xs,
      borderColor: colors.border.light,
    },
  },

  // Bordered card (border only, no shadow)
  bordered: {
    bordered: true,
    style: {
      borderRadius: borderRadius.md,
      boxShadow: 'none',
      borderColor: colors.border.light,
    },
  },

  // Elevated card (subtle shadow with border)
  elevated: {
    bordered: true,
    style: {
      borderRadius: borderRadius.md,
      boxShadow: shadows.sm,
      borderColor: colors.border.light,
    },
  },

  // Compact card (less padding)
  compact: {
    size: 'small',
    style: {
      borderRadius: borderRadius.sm,
    },
  },

  // Metric/stat card
  metric: {
    style: {
      borderRadius: borderRadius.md,
      boxShadow: shadows.sm,
      textAlign: 'center',
      padding: spacing.lg,
    },
  },

  // Danger/alert card
  danger: {
    style: {
      borderRadius: borderRadius.md,
      backgroundColor: colors.status.failBg,
      borderColor: colors.status.fail,
    },
  },

  // Warning card
  warning: {
    style: {
      borderRadius: borderRadius.md,
      backgroundColor: colors.status.warningBg,
      borderColor: colors.status.warning,
    },
  },

  // Success card
  success: {
    style: {
      borderRadius: borderRadius.md,
      backgroundColor: colors.status.passBg,
      borderColor: colors.status.pass,
    },
  },
};

// ==================== TABLE VARIANTS ====================

export const tableVariants = {
  // Default table
  default: {
    size: 'middle',
    bordered: false,
    pagination: {
      showSizeChanger: true,
      showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
      pageSize: 20,
      pageSizeOptions: ['10', '20', '50', '100'],
    },
  },

  // Compact table (for dense data)
  compact: {
    size: 'small',
    bordered: true,
    pagination: {
      size: 'small',
      showSizeChanger: true,
      pageSize: 50,
      pageSizeOptions: ['20', '50', '100', '200'],
    },
  },

  // Expandable table (for nested data)
  expandable: {
    size: 'middle',
    bordered: false,
    expandable: {
      expandIconColumnIndex: 0,
    },
  },

  // No pagination (for small datasets)
  noPagination: {
    size: 'middle',
    bordered: false,
    pagination: false,
  },
};

// ==================== MODAL VARIANTS ====================

export const modalVariants = {
  // Default modal
  default: {
    centered: true,
    width: 600,
    style: {
      borderRadius: borderRadius.lg,
    },
  },

  // Large modal
  large: {
    centered: true,
    width: 900,
    style: {
      borderRadius: borderRadius.lg,
    },
  },

  // Full width modal
  fullWidth: {
    centered: true,
    width: '90%',
    style: {
      borderRadius: borderRadius.lg,
      maxWidth: 1200,
    },
  },

  // Form modal
  form: {
    centered: true,
    width: 700,
    style: {
      borderRadius: borderRadius.lg,
    },
    destroyOnClose: true,
  },

  // Confirmation modal
  confirm: {
    centered: true,
    width: 450,
    style: {
      borderRadius: borderRadius.lg,
    },
  },
};

// ==================== SCORE DISPLAY ====================

/**
 * Get trust score color based on value
 * Tier-based: 80-100 (excellent), 60-79 (good), <60 (poor)
 */
export const getTrustScoreColor = (score) => {
  if (score >= 80) return colors.trustScore.excellent;
  if (score >= 60) return colors.trustScore.good;
  return colors.trustScore.poor;
};

/**
 * Get trust score status (for Ant Design Tag/Badge)
 * Tier-based: 80-100 (excellent), 60-79 (good), <60 (poor)
 */
export const getTrustScoreStatus = (score) => {
  if (score >= 80) return 'success';
  if (score >= 60) return 'warning';
  return 'error';
};

/**
 * Score display variants
 */
export const scoreVariants = {
  // Large gauge display
  gauge: {
    size: 200,
    strokeWidth: 12,
    format: (percent) => `${Math.round(percent)}`,
  },

  // Medium gauge
  gaugeMedium: {
    size: 150,
    strokeWidth: 10,
    format: (percent) => `${Math.round(percent)}`,
  },

  // Small gauge
  gaugeSmall: {
    size: 100,
    strokeWidth: 8,
    format: (percent) => `${Math.round(percent)}`,
  },

  // Progress bar
  progress: {
    strokeWidth: 12,
    showInfo: true,
    format: (percent) => `${Math.round(percent)}%`,
  },
};

// ==================== SECTION/PANEL STYLES ====================

export const sectionStyles = {
  // Page header section
  pageHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: spacing.lg,
  },

  // Filter bar section
  filterBar: {
    display: 'flex',
    gap: spacing.md,
    marginBottom: spacing.lg,
    flexWrap: 'wrap',
    alignItems: 'center',
  },

  // Content section
  contentSection: {
    padding: spacing.lg,
    backgroundColor: colors.background.paper,
    borderRadius: borderRadius.md,
    boxShadow: shadows.sm,
    marginBottom: spacing.lg,
  },

  // Stat card content
  statCard: {
    textAlign: 'center',
    padding: spacing.lg,
  },

  // Empty state
  emptyState: {
    textAlign: 'center',
    padding: `${spacing.xxl}px ${spacing.lg}px`,
    color: colors.text.secondary,
  },

  // Error state
  errorState: {
    textAlign: 'center',
    padding: `${spacing.xxl}px ${spacing.lg}px`,
    color: colors.status.fail,
  },
};

// ==================== ICON SIZES ====================

export const iconSizes = {
  xs: 12,
  sm: 14,
  md: 16,
  lg: 20,
  xl: 24,
  xxl: 32,
};

// ==================== HELPER FUNCTIONS ====================

/**
 * Generate consistent spacing style object
 */
export const getSpacingStyle = (padding, margin) => {
  const style = {};

  if (padding) {
    if (typeof padding === 'number') {
      style.padding = spacing[Object.keys(spacing)[padding]] || padding;
    } else if (typeof padding === 'object') {
      if (padding.top) style.paddingTop = spacing[Object.keys(spacing)[padding.top]];
      if (padding.bottom) style.paddingBottom = spacing[Object.keys(spacing)[padding.bottom]];
      if (padding.left) style.paddingLeft = spacing[Object.keys(spacing)[padding.left]];
      if (padding.right) style.paddingRight = spacing[Object.keys(spacing)[padding.right]];
    }
  }

  if (margin) {
    if (typeof margin === 'number') {
      style.margin = spacing[Object.keys(spacing)[margin]] || margin;
    } else if (typeof margin === 'object') {
      if (margin.top) style.marginTop = spacing[Object.keys(spacing)[margin.top]];
      if (margin.bottom) style.marginBottom = spacing[Object.keys(spacing)[margin.bottom]];
      if (margin.left) style.marginLeft = spacing[Object.keys(spacing)[margin.left]];
      if (margin.right) style.marginRight = spacing[Object.keys(spacing)[margin.right]];
    }
  }

  return style;
};

/**
 * Merge variant with custom props
 */
export const mergeVariant = (variant, customProps = {}) => {
  return {
    ...variant,
    ...customProps,
    style: {
      ...variant.style,
      ...customProps.style,
    },
  };
};

export default {
  buttonVariants,
  cardVariants,
  tableVariants,
  modalVariants,
  scoreVariants,
  sectionStyles,
  iconSizes,
  getStatusTagColor,
  getSeverityTagProps,
  getTrustScoreColor,
  getTrustScoreStatus,
  getSpacingStyle,
  mergeVariant,
};
